import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { applyColorMode, onSystemColorModeChange, readColorMode, saveColorMode, type ColorMode } from './color-mode';
import { defaultTheme, themeStyle, themeVariables, type Theme } from './theme';

interface ThemeContextValue {
  theme: Theme;
  /** Theme for the current screen (an agency's, a public page's); resetTheme returns to the base */
  setTheme: (theme: Theme) => void;
  resetTheme: () => void;
  /** The look outside any agency: the platform's, or the agency whose custom domain this is */
  setBaseTheme: (theme: Theme) => void;
  /** Light, dark or follow the system */
  colorMode: ColorMode;
  resolvedColorMode: 'light' | 'dark';
  setColorMode: (mode: ColorMode) => void;
  /**
   * The browser tab's title while a screen is shown (most specific part first, e.g.
   * ["Contacts", "Acme Fitness"]); the brand name is appended. null: the brand name alone.
   */
  setPageTitle: (parts: string[] | null) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function setFavicon(href: string | undefined): void {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="icon"][data-brand]');
  if (!href) {
    link?.remove();
    return;
  }
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    link.dataset.brand = 'true';
    document.head.appendChild(link);
  }
  link.href = href;
}

export function ThemeProvider({ children, initialTheme = defaultTheme }: { children: ReactNode; initialTheme?: Theme }) {
  const [base, setBaseTheme] = useState(initialTheme);
  const [override, setOverride] = useState<Theme | null>(null);
  const [pageTitle, setPageTitleState] = useState<string | null>(null);
  const [colorMode, setColorModeState] = useState<ColorMode>(readColorMode);
  const [resolvedColorMode, setResolved] = useState<'light' | 'dark'>(() => applyColorMode(colorMode));
  const theme = override ?? base;
  const setTheme = useCallback((next: Theme) => setOverride(next), []);
  const resetTheme = useCallback(() => setOverride(null), []);
  const setPageTitle = useCallback((parts: string[] | null) => {
    const clean = (parts ?? []).map((p) => p.trim()).filter(Boolean);
    setPageTitleState(clean.length ? clean.join(' · ') : null);
  }, []);
  const setColorMode = useCallback((mode: ColorMode) => {
    saveColorMode(mode);
    setColorModeState(mode);
  }, []);

  useEffect(() => {
    setResolved(applyColorMode(colorMode));
    if (colorMode !== 'system') return undefined;
    return onSystemColorModeChange(() => setResolved(applyColorMode('system')));
  }, [colorMode]);

  const value = useMemo(
    () => ({ theme, setTheme, resetTheme, setBaseTheme, colorMode, resolvedColorMode, setColorMode, setPageTitle }),
    [theme, setTheme, resetTheme, colorMode, resolvedColorMode, setColorMode, setPageTitle]
  );

  // White-label: the tab icon follows the brand, and the title names the screen, then the brand (WCAG 2.4.2)
  useEffect(() => {
    setFavicon(theme.faviconUrl);
  }, [theme.faviconUrl]);
  useEffect(() => {
    document.title = pageTitle && pageTitle !== theme.brandName ? `${pageTitle} · ${theme.brandName}` : theme.brandName;
  }, [pageTitle, theme.brandName]);

  // Menus, dialogs and tooltips render in portals outside .theme-root: give them the brand too
  useEffect(() => {
    const root = document.documentElement.style;
    const vars = themeVariables(theme);
    for (const [name, val] of Object.entries(vars)) root.setProperty(name, val);
    return () => {
      for (const name of Object.keys(vars)) root.removeProperty(name);
    };
  }, [theme]);

  return (
    <ThemeContext.Provider value={value}>
      <div className="theme-root" style={themeStyle(theme)}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme must be used inside <ThemeProvider>');
  return value;
}
